/**
 * useAgent Hook
 * 
 * React hook for interacting with the agent service.
 * Provides:
 * - Chat functionality with streaming
 * - Conversation management
 * - Tool execution tracking
 * - Content state management
 * 
 * @module useAgent
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import agentService from '../services/agentService';

/**
 * Content types for the agent
 */
export const ContentTypes = {
    POST: 'post',
    REELS: 'reels',
    COMING_SOON: 'coming_soon'
};

/**
 * Platform types
 */
export const Platforms = {
    LINKEDIN: 'linkedin',
    TWITTER: 'twitter',
    FACEBOOK: 'facebook',
    INSTAGRAM: 'instagram'
};

/**
 * useAgent hook
 * 
 * @param {object} config - Configuration options
 * @param {string} config.episodeId - Episode ID (required)
 * @param {string} config.platform - Platform context
 * @param {string} config.contentType - Content type
 * @param {object} config.context - Additional context (guestName, guestBio, etc.)
 * @param {function} config.onContentChange - Callback when content changes
 * @param {function} config.onImageUrlChange - Callback when image URL changes
 * @returns {object} Hook state and actions
 */
export const useAgent = ({
    episodeId,
    platform = 'linkedin',
    contentType = 'post',
    context = {},
    onContentChange,
    onImageUrlChange
}) => {
    // Conversation state
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);

    // Loading and streaming state
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);

    // Tool execution state
    const [activeTool, setActiveTool] = useState(null);
    const [executedTools, setExecutedTools] = useState([]);

    // Content state
    const [generatedContent, setGeneratedContent] = useState('');
    const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

    // Streaming buffer
    const streamBufferRef = useRef('');
    const contentBufferRef = useRef('');

    // Callback refs
    const onContentChangeRef = useRef(onContentChange);
    const onImageUrlChangeRef = useRef(onImageUrlChange);

    // Keep refs updated
    useEffect(() => {
        onContentChangeRef.current = onContentChange;
    }, [onContentChange]);

    useEffect(() => {
        onImageUrlChangeRef.current = onImageUrlChange;
    }, [onImageUrlChange]);

    /**
     * Load existing conversation on mount
     */
    useEffect(() => {
        if (episodeId) {
            loadConversation();
        }
    }, [episodeId]);

    /**
     * Send a message to the agent
     * 
     * @param {string} message - User's message
     * @param {object} options - Additional options
     * @returns {Promise<object>} Result
     */
    const sendMessage = useCallback(async (message, options = {}) => {
        if (!episodeId) {
            setError('Episode ID is required');
            return { success: false, error: 'Episode ID is required' };
        }

        // Reset state
        setError(null);
        setIsLoading(true);
        setIsStreaming(true);
        streamBufferRef.current = '';
        contentBufferRef.current = '';
        setActiveTool(null);

        // Add user message to local state
        const userMessage = {
            id: `user_${Date.now()}`,
            role: 'user',
            content: message,
            timestamp: Date.now()
        };
        setMessages(prev => [...prev, userMessage]);

        try {
            const result = await agentService.chat({
                episodeId,
                message,
                platform: options.platform || platform,
                contentType: options.contentType || contentType,
                conversationId,
                context: {
                    ...context,
                    currentContent: generatedContent,
                    currentImageUrl: generatedImageUrl
                },
                onToolStart: (toolInfo) => {
                    setActiveTool(toolInfo);
                    setExecutedTools(prev => [...prev, { ...toolInfo, status: 'running' }]);
                },
                onToolComplete: (toolInfo) => {
                    setActiveTool(null);
                    setExecutedTools(prev =>
                        prev.map(t =>
                            t.tool === toolInfo.tool
                                ? { ...t, status: 'completed', result: toolInfo.result }
                                : t
                        )
                    );
                },
                onToken: (token) => {
                    streamBufferRef.current += token;
                },
                onContentToken: (token) => {
                    contentBufferRef.current += token;
                    setGeneratedContent(prev => prev + token);
                    onContentChangeRef.current?.(contentBufferRef.current);
                },
                onComplete: (data) => {
                    // Add assistant message
                    const assistantMessage = {
                        id: `assistant_${Date.now()}`,
                        role: 'assistant',
                        content: data.response,
                        timestamp: Date.now(),
                        toolCalls: data.executedTools
                    };
                    setMessages(prev => [...prev, assistantMessage]);

                    // Update conversation ID
                    if (data.conversationId) {
                        setConversationId(data.conversationId);
                    }

                    // Update content if generated
                    if (data.generatedContent) {
                        setGeneratedContent(data.generatedContent);
                        onContentChangeRef.current?.(data.generatedContent);
                    }

                    // Update image URL if generated
                    if (data.generatedImageUrl) {
                        setGeneratedImageUrl(data.generatedImageUrl);
                        onImageUrlChangeRef.current?.(data.generatedImageUrl);
                    }
                },
                onError: (err) => {
                    setError(err.message);
                }
            });

            setIsLoading(false);
            setIsStreaming(false);

            return result;

        } catch (err) {
            setError(err.message);
            setIsLoading(false);
            setIsStreaming(false);

            return { success: false, error: err.message };
        }
    }, [episodeId, platform, contentType, context, conversationId, generatedContent, generatedImageUrl]);

    /**
     * Start a new conversation
     * 
     * @param {object} options - Options for new conversation
     * @returns {Promise<object>} New conversation
     */
    const startNewConversation = useCallback(async (options = {}) => {
        if (!episodeId) {
            setError('Episode ID is required');
            return { success: false, error: 'Episode ID is required' };
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await agentService.startNewConversation({
                episodeId,
                platform: options.platform || platform,
                contentType: options.contentType || contentType,
                context: options.context || context
            });

            if (result.success) {
                setConversationId(result.conversation.id);
                setMessages([]);
                setExecutedTools([]);
                setGeneratedContent('');
                setGeneratedImageUrl(null);
            }

            setIsLoading(false);
            return result;

        } catch (err) {
            setError(err.message);
            setIsLoading(false);
            return { success: false, error: err.message };
        }
    }, [episodeId, platform, contentType, context]);

    /**
     * Load existing conversation
     * 
     * @returns {Promise<object>} Conversation
     */
    const loadConversation = useCallback(async () => {
        if (!episodeId) return null;

        setIsLoading(true);

        try {
            const result = await agentService.getCurrentConversation(episodeId);

            if (result.success && result.conversation) {
                setConversationId(result.conversation.id);
                setMessages(result.conversation.messages || []);
                setGeneratedContent(result.conversation.context?.currentContent || '');
                setGeneratedImageUrl(result.conversation.context?.currentImageUrl || null);
            }

            setIsLoading(false);
            return result;

        } catch (err) {
            setError(err.message);
            setIsLoading(false);
            return null;
        }
    }, [episodeId]);

    /**
     * Clear current conversation
     * 
     * @returns {Promise<object>} Result
     */
    const clearConversation = useCallback(async () => {
        if (!episodeId) return { success: false };

        setIsLoading(true);

        try {
            const result = await agentService.clearConversation(episodeId);

            if (result.success) {
                setConversationId(null);
                setMessages([]);
                setExecutedTools([]);
                setGeneratedContent('');
                setGeneratedImageUrl(null);
            }

            setIsLoading(false);
            return result;

        } catch (err) {
            setError(err.message);
            setIsLoading(false);
            return { success: false, error: err.message };
        }
    }, [episodeId]);

    /**
     * Cancel ongoing stream
     */
    const cancelStream = useCallback(() => {
        agentService.cancelStream();
        setIsStreaming(false);
        setIsLoading(false);
        setActiveTool(null);
    }, []);

    /**
     * Get available tools for current content type
     * 
     * @returns {Promise<object>} Available tools
     */
    const getAvailableTools = useCallback(async () => {
        return agentService.getAvailableTools(contentType);
    }, [contentType]);

    /**
     * Update context
     * 
     * @param {object} newContext - New context values
     */
    const updateContext = useCallback((newContext) => {
        context = { ...context, ...newContext };
    }, []);

    /**
     * Clear error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // State
        conversationId,
        messages,
        isLoading,
        isStreaming,
        error,
        activeTool,
        executedTools,
        generatedContent,
        generatedImageUrl,

        // Actions
        sendMessage,
        startNewConversation,
        loadConversation,
        clearConversation,
        cancelStream,
        getAvailableTools,
        updateContext,
        clearError,

        // Utilities
        hasConversation: !!conversationId,
        messageCount: messages.length,
        hasContent: !!generatedContent,
        hasImage: !!generatedImageUrl
    };
};

export default useAgent;